import { Button, DialogTrigger, makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { KeyboardEventArgs } from '@syncfusion/ej2-base';
import {
    CellEditArgs,
    CellSaveArgs,
    ColumnModel,
    Edit,
    EditMode,
    Freeze,
    GridComponent,
    Inject,
    NewRowPosition,
    Resize,
    RowDataBoundEventArgs,
    RowSelectingEventArgs,
    SelectionSettingsModel,
    Toolbar,
} from '@syncfusion/ej2-react-grids';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditableGridStyles } from '../../../hooks/useEditableGridStyles';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { ColumnDto, ColumnGridDataType, ColumnType, DetailedWorkspaceDto, RowDto } from '../../../model';
import { ROW_NO_COLUMN_NAME } from '../../../utils/DetailedWorkspace.constants';
import {
    cancelEditModeOnShiftTab,
    formatNumericCellDisplayForGrid,
    getGridColumnEditType,
    IFormViewerMethods,
    ImperativeComponent,
    parseNumericValue,
    transformColumnarReportToTableData,
} from '../../../utils/DetailedWorkspaceUtils';
import {
    attachSyncfusionDeleteToolbarFluentTooltip,
    cancelScheduledToolbarFlushRaf,
    createInitialToolbarFlushRafHandle,
    getSyncfusionToolbarModule,
    scheduleToolbarFlushRaf,
    syncfusionDeleteToolbarItemId,
    type IToolbarFluentTooltipState,
} from '../../../utils/SyncfusionGridToolbarUtils';
import {
    enableSingleClickEditOnCells,
    getAffectedColumns,
    getAffectedRows,
    getInputNameFromClipboardEvent,
    isExcelClipboardContent,
    parsePasteContentToColumnarReportTable,
    TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE,
} from '../../../utils/TableUtils';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import DefaultGrid from '../../grid/DefaultGrid';

const DATA_COLUMN_MIN_WIDTH = '14ch';

const FIRST_COLUMN_WIDTH_PX = 220;

/** Prevents duplicate listeners if `dataBound` runs again on the same toolbar node. */
const DELETE_TOOLBAR_FLUENT_TOOLTIP_ATTR = 'data-column-delete-fluent-tooltip';

export interface IColumnTableFormViewerProps {
    detailedWorkspace: DetailedWorkspaceDto | null | undefined;
    onDataChange?: () => void;
}

const useStyles = makeStyles({
    byColumnStatementStyles: {
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        '& .e-grid .e-gridheader .e-headercell': {
            backgroundColor: `#C7DCE5 !important`,
        },
        // Vertical grid lines only: no horizontal rules on header chrome or row boundaries
        '& .e-grid.e-verticallines .e-gridheader': {
            borderTop: 'none',
            borderBottom: 'none',
        },
        '& .e-grid.e-verticallines .e-rowcell.e-lastrowcell:not(.e-xlsel-bottom-border)': {
            borderBottomWidth: 0,
            borderBottomStyle: 'none',
        },
        '& .e-grid.e-verticallines .e-firstchildrow .e-rowcell': {
            borderTopWidth: 0,
            borderTopStyle: 'none',
        },
        '& .disabled-row > td': {
            color: `gray !important`,
        },
        '& .hidden-row > td': {
            display: 'none',
        },
        '& .text-bold': {
            fontWeight: tokens.fontWeightSemibold,
        },
        '& .e-rowcell.text-bold.e-cellselectionbackground': {
            fontWeight: tokens.fontWeightSemibold,
        },
        '& th div.e-rhandler.e-rcursor': {
            display: 'none',
        },
    },
    tableContentClass: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        height: '100%',
        minWidth: 0,
    },
});

function getFirstEditableDataRowIndex(grid: GridComponent, detailedWorkspace: DetailedWorkspaceDto | null | undefined): number {
    const rows = grid.getDataModule()?.dataManager?.dataSource?.json as Record<string, unknown>[] | undefined;
    if (!Array.isArray(rows) || !detailedWorkspace?.rowSchema) {
        return 0;
    }
    for (let i = 0; i < rows.length; i++) {
        const rowNo = rows[i]?.RowNo as number | undefined;
        if (rowNo == null) {
            continue;
        }
        if (!detailedWorkspace.rowSchema[rowNo]?.disable) {
            return i;
        }
    }
    return 0;
}

function isNumericDataColumnCell(
    field: string | undefined,
    rowNo: number | undefined,
    detailedWorkspace: DetailedWorkspaceDto | null | undefined
): boolean {
    if (!field || rowNo == null || !detailedWorkspace?.rowSchema) {
        return false;
    }
    if (field === ROW_NO_COLUMN_NAME || field === detailedWorkspace.firstColumn?.field) {
        return false;
    }
    return detailedWorkspace.rowSchema[rowNo]?.dataType === ColumnGridDataType.Numeric;
}

/** When `digitsOnly` is true, parse like in-cell editor text (non-digits stripped). Otherwise parse model/API values (commas/spaces stripped, full Number parse). */
function numericValueForDirtyCompare(value: unknown, digitsOnly: boolean): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (!digitsOnly && typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
    }
    const str = String(value).trim();
    if (str === '') {
        return null;
    }
    const paren = /^\(([\d,\s]+)\)$/.exec(str);
    if (paren) {
        const n = Number(paren[1].replace(/,/g, '').replace(/\s/g, ''));
        return Number.isNaN(n) ? null : -n;
    }
    if (digitsOnly) {
        const d = str.replace(/\D/g, '');
        if (d === '') {
            return null;
        }
        const n = Number(d);
        return Number.isNaN(n) ? null : n;
    }
    const n = Number(str.replace(/,/g, '').replace(/\s/g, ''));
    return Number.isNaN(n) ? null : n;
}

function batchEditValueChanged(
    current: string,
    textBaseline: string | null,
    isNumeric: boolean,
    numericModelBaseline: number | null
): boolean {
    if (isNumeric) {
        const cur = numericValueForDirtyCompare(current, true);
        if (numericModelBaseline === null && cur === null) {
            return false;
        }
        if (numericModelBaseline === null || cur === null) {
            return true;
        }
        return cur !== numericModelBaseline;
    }
    if (textBaseline === null) {
        return false;
    }
    return current !== textBaseline;
}

function numericValuesDiffer(previous: unknown, current: unknown): boolean {
    const prev = numericValueForDirtyCompare(previous, false);
    const next = numericValueForDirtyCompare(current, false);
    if (prev === null && next === null) {
        return false;
    }
    if (prev === null || next === null) {
        return true;
    }
    return prev !== next;
}

const ColumnTableFormViewer: ImperativeComponent<IFormViewerMethods, IColumnTableFormViewerProps> = React.forwardRef<
    IFormViewerMethods,
    IColumnTableFormViewerProps
>(({ detailedWorkspace, onDataChange }, gridRef) => {
    const { functionalAccess } = useFunctionalAccess();
    const innerGridRef = useRef<GridComponent>(null);
    const [currentTooltipState, setCurrentTooltipState] = useState<IToolbarFluentTooltipState>({ text: null, target: null });
    const [columnDeleteConfirmOpen, setColumnDeleteConfirmOpen] = useState(false);
    const pendingDeleteColumnFieldRef = useRef<string | null>(null);
    const deleteToolbarFlushRafRef = useRef(createInitialToolbarFlushRafHandle());
    const pendingDeleteColumnFieldSnapshotRef = useRef<string | null>(null);
    const focusFirstCellFieldAfterColumnAddRef = useRef<string | null>(null);
    const onDataChangeRef = useRef(onDataChange);
    onDataChangeRef.current = onDataChange;

    useEffect(() => {
        return () => cancelScheduledToolbarFlushRaf(deleteToolbarFlushRafRef);
    }, []);

    const editValueChangedRef = useRef<boolean>(false);
    const activeBatchEditBaselineRef = useRef<string | null>(null);
    const activeBatchEditNumericRef = useRef(false);
    const activeBatchEditNumericModelBaselineRef = useRef<number | null>(null);

    const hasUnsavedEditChanges = () => {
        const grid = innerGridRef.current;
        if (!grid) return false;

        const batchChanges = grid.editModule?.getBatchChanges?.() as
            | {
                  addedRecords?: unknown[];
                  changedRecords?: unknown[];
                  deletedRecords?: unknown[];
              }
            | undefined;
        if (batchChanges) {
            const hasChanges =
                (batchChanges.addedRecords?.length ?? 0) > 0 ||
                (batchChanges.changedRecords?.length ?? 0) > 0 ||
                (batchChanges.deletedRecords?.length ?? 0) > 0;
            if (hasChanges) return true;
        }

        if (grid.isEdit) {
            if (editValueChangedRef.current) return true;

            const activeInput = grid.element.querySelector('.e-editedbatchcell input, .e-editedbatchcell textarea') as
                | HTMLInputElement
                | HTMLTextAreaElement
                | null;
            if (
                activeInput &&
                batchEditValueChanged(
                    activeInput.value,
                    activeBatchEditBaselineRef.current,
                    activeBatchEditNumericRef.current,
                    activeBatchEditNumericModelBaselineRef.current
                )
            ) {
                return true;
            }
        }

        return false;
    };

    useImperativeHandle(gridRef, () => ({
        getUpdatedDetailedWorkspace,
        hasUnsavedEditChanges,
    }));

    const styles = useStyles();
    const { gridContainerStyles, toolbarStyles, footerStyles } = useEditableGridStyles();
    const byColumnStatementClass = mergeClasses(gridContainerStyles, toolbarStyles, footerStyles, styles.byColumnStatementStyles);
    const { t } = useTranslation();
    const deleteToolbarTooltipTextRef = useRef('');
    deleteToolbarTooltipTextRef.current = t('deleteColumnToolbarTooltip');

    const toolbarOptions = ['Add', 'Delete'];

    const editOptions = {
        allowEditing: functionalAccess?.amountDataEntryModifyEnabled ?? false,
        allowAdding: functionalAccess?.amountDataEntryModifyEnabled ?? false,
        newRowPosition: 'Bottom' as NewRowPosition,
        mode: 'Batch' as EditMode,
        showConfirmDialog: false,
    };

    const selectionSettings: SelectionSettingsModel = { mode: 'Cell' };

    const { columns, dataSource } = useMemo(() => {
        return transformColumnarReportToTableData(detailedWorkspace, true);
    }, [detailedWorkspace]);

    const getUpdatedColumns = (): ColumnDto[] => {
        const allGridColumns = innerGridRef.current?.columns || [];
        const gridColumns = allGridColumns.filter((col): col is ColumnModel => typeof col === 'object' && col !== null && 'field' in col);

        const existingColumns = detailedWorkspace?.columns || [];
        const existingByField = new Map(
            existingColumns.filter((c): c is ColumnDto & { field: string } => !!c.field).map((c) => [c.field, c])
        );

        const rowNoColumnDto = existingColumns.find((c) => c.field === ROW_NO_COLUMN_NAME);

        const dataColumns: ColumnDto[] = gridColumns
            .filter((col) => col.field && col.field !== ROW_NO_COLUMN_NAME)
            .map((col) => {
                const field = col.field as string;
                const existing = existingByField.get(field);
                const headerText = col.headerText ?? '';

                if (existing) {
                    return {
                        field: existing.field,
                        type: existing.type,
                        name: headerText !== '' ? headerText : existing.name ?? '',
                        summary: existing.summary ?? '',
                        columnAlignment: existing.columnAlignment,
                    };
                }

                return {
                    field,
                    type: ColumnType.Text,
                    name: headerText,
                    summary: '',
                };
            });

        return rowNoColumnDto ? [rowNoColumnDto, ...dataColumns] : dataColumns;
    };

    const getUpdatedDetailedWorkspace = () => {
        innerGridRef.current?.editModule.batchSave();
        const rawRows = innerGridRef.current?.getDataModule().dataManager.dataSource.json as Record<string, unknown>[] | undefined;
        const columns = getUpdatedColumns();

        const savedColumnFields = new Set(columns.map((c) => c.field).filter((f): f is string => !!f));
        const previousSchemaFields = new Set((detailedWorkspace?.columns || []).map((c) => c.field).filter((f): f is string => !!f));

        const updatedRows =
            rawRows?.map((row) => {
                const next = { ...row };
                for (const key of Object.keys(next)) {
                    if (previousSchemaFields.has(key) && !savedColumnFields.has(key)) {
                        delete next[key];
                    }
                }
                return next;
            }) ?? rawRows;

        const updatedDetailedWorkspace: DetailedWorkspaceDto = {
            ...detailedWorkspace,
            rows: updatedRows as any,
            columns,
        };

        return updatedDetailedWorkspace;
    };

    const addTooltipEventHandlers = (cell: HTMLElement) => {
        if (cell) {
            cell.onmouseenter = () => {
                if (cell.scrollWidth > cell.clientWidth) {
                    setCurrentTooltipState({ text: cell.innerText, target: cell });
                }
            };

            cell.onmouseleave = () => {
                setCurrentTooltipState({ text: null, target: null });
            };
        }
    };

    const onQueryCellInfo = (args: any) => {
        const cell = args.cell as HTMLElement;
        addTooltipEventHandlers(cell);

        const column = args.column as ColumnModel | undefined;
        const rowData = args.data as Record<string, unknown> | undefined;
        const field = column?.field;
        const rowNo = rowData?.RowNo as number | undefined;
        if (!field || !rowData || !isNumericDataColumnCell(field, rowNo, detailedWorkspace)) {
            return;
        }
        if (cell.closest('.e-editedbatchcell')) {
            return;
        }
        cell.textContent = formatNumericCellDisplayForGrid(rowData[field]);
    };

    const gridColumns: ColumnModel[] = useMemo(() => {
        const cols: ColumnModel[] = [
            {
                field: ROW_NO_COLUMN_NAME,
                width: 50,
                isPrimaryKey: true,
                visible: false,
            },
        ];

        const firstColumnBatchEdit = {
            write: (args: any) => {
                const rowNo = args.rowData?.['RowNo'] as number | undefined;
                const maxLength = rowNo != null ? detailedWorkspace?.rowSchema?.[rowNo]?.maxLength : undefined;
                if (maxLength) {
                    args.element.maxLength = maxLength;
                }
                const input = args.element as HTMLInputElement;
                activeBatchEditNumericModelBaselineRef.current = null;
                activeBatchEditBaselineRef.current = input.value;
                activeBatchEditNumericRef.current = false;
                return true;
            },
        };

        const dataColumnBatchEdit = {
            write: (args: any) => {
                const rowNo = args.rowData?.['RowNo'] as number | undefined;
                const cellDef = rowNo != null ? detailedWorkspace?.rowSchema?.[rowNo] : undefined;
                if (cellDef?.maxLength) {
                    args.element.maxLength = cellDef.maxLength;
                }
                const input = args.element as HTMLInputElement;
                const field = args.column?.field as string | undefined;
                const isNumeric = cellDef?.dataType === ColumnGridDataType.Numeric;
                if (isNumeric) {
                    const raw = field != null ? args.rowData?.[field] : undefined;
                    activeBatchEditNumericModelBaselineRef.current = numericValueForDirtyCompare(raw, false);
                    input.value = String(parseNumericValue(input.value));
                    input.onkeydown = (e: KeyboardEvent) => {
                        if (e.ctrlKey || e.metaKey || e.altKey) {
                            return;
                        }
                        if (e.key.length === 1 && (e.key < '0' || e.key > '9')) {
                            e.preventDefault();
                        }
                    };
                    input.oninput = () => {
                        input.value = String(parseNumericValue(input.value));
                    };
                } else {
                    activeBatchEditNumericModelBaselineRef.current = null;
                }
                activeBatchEditBaselineRef.current = input.value;
                activeBatchEditNumericRef.current = !!isNumeric;
                return true;
            },
        };

        columns.forEach((col) => {
            const isFirstColumnAfterRowNo = col.field === detailedWorkspace?.firstColumn?.field;
            const columnName = isFirstColumnAfterRowNo ? t('taxpayerInformation') : '';

            cols.push({
                field: col.field || '',
                headerText: columnName,
                autoFit: isFirstColumnAfterRowNo ? true : false,
                maxWidth: 250,
                visible: col.field !== ROW_NO_COLUMN_NAME,
                isFrozen: isFirstColumnAfterRowNo ? true : false,
                freeze: isFirstColumnAfterRowNo ? 'Left' : 'None',
                customAttributes: isFirstColumnAfterRowNo ? { class: 'text-bold' } : undefined,
                editType: 'textbox',
                edit: isFirstColumnAfterRowNo ? firstColumnBatchEdit : dataColumnBatchEdit,
                type: 'string',
                format: '',
                allowEditing: isFirstColumnAfterRowNo ? false : editOptions.allowEditing,
                width: isFirstColumnAfterRowNo ? FIRST_COLUMN_WIDTH_PX : DATA_COLUMN_MIN_WIDTH,
                minWidth: isFirstColumnAfterRowNo ? FIRST_COLUMN_WIDTH_PX : DATA_COLUMN_MIN_WIDTH,
            });
        });

        return cols;
    }, [columns, detailedWorkspace, editOptions.allowEditing, t]);

    const customPasteHandler = useCallback(
        (event: ClipboardEvent, selectedRowDefinition: RowDto | undefined) => {
            if (isExcelClipboardContent(event)) {
                event.preventDefault();

                const currentColumns = getUpdatedColumns();
                const pastedText = event.clipboardData?.getData(TEXT_PLAIN_CLIPBOARD_CONTENT_TYPE);

                const initialColumnFieldName = getInputNameFromClipboardEvent(event);
                const affectedColumns = getAffectedColumns(currentColumns, initialColumnFieldName);
                const affectedColumnsNames = affectedColumns.map((col) => col.field || '');
                const affectedRows = getAffectedRows(
                    dataSource,
                    parseInt(selectedRowDefinition?.sKey ?? '0'),
                    detailedWorkspace?.rowSchema
                );
                const parsedContent = parsePasteContentToColumnarReportTable(
                    pastedText ?? '',
                    currentColumns,
                    affectedColumnsNames,
                    affectedRows,
                    detailedWorkspace?.rowSchema
                );

                if (innerGridRef.current && parsedContent.length > 0) {
                    if (event.target) {
                        (event.target as HTMLInputElement).value = parsedContent[0][initialColumnFieldName];
                    }

                    const updatedColumnNames = Object.keys(parsedContent[0]);
                    const newColumnNames = updatedColumnNames.filter((col: string) => !currentColumns.some((c) => c.field === col));

                    if (newColumnNames.length > 0) {
                        addColumns(newColumnNames.length, false);
                    }

                    innerGridRef.current.batchUpdate({
                        changedRecords: parsedContent,
                    });
                }
            }

            onDataChange?.();
        },
        [columns]
    );

    if (!detailedWorkspace || !columns.length || !dataSource.length) {
        return <div className={styles.tableContentClass}>{t('noDataAvailable')}</div>;
    }

    const getSelectedDeletableColumnField = (grid: GridComponent): string | null => {
        const firstField = detailedWorkspace.firstColumn?.field;

        const asDeletableField = (field: string | null | undefined): string | null => {
            if (!field || field === ROW_NO_COLUMN_NAME || field === firstField) {
                return null;
            }
            return field;
        };

        const selected = grid.getSelectedRowCellIndexes?.() ?? [];
        let minColIdx: number | null = null;
        for (const block of selected) {
            for (const ci of block.cellIndexes ?? []) {
                minColIdx = minColIdx === null ? ci : Math.min(minColIdx, ci);
            }
        }
        if (minColIdx === null && grid.isEdit) {
            const editedCell = grid.element.querySelector('.e-editedbatchcell') as HTMLElement | null;
            if (editedCell) {
                const ariaCol = editedCell.getAttribute('aria-colindex');
                const parsed = ariaCol != null ? parseInt(ariaCol, 10) : NaN;
                if (!Number.isNaN(parsed)) {
                    minColIdx = parsed - 1;
                }
            }
        }
        if (minColIdx != null) {
            const column = grid.getColumnByIndex(minColIdx) as ColumnModel | undefined;
            const fromIndex = asDeletableField(column?.field);
            if (fromIndex) {
                return fromIndex;
            }
        }

        return null;
    };

    const flushDeleteToolbarItemState = () => {
        const grid = innerGridRef.current;
        const toolbarModule = getSyncfusionToolbarModule(grid);
        const id = grid?.element?.id;
        if (!id || !toolbarModule) {
            return;
        }

        const field = getSelectedDeletableColumnField(grid);
        const deleteEnabled = field != null && functionalAccess?.amountDataEntryModifyEnabled !== false;

        toolbarModule.enableItems([syncfusionDeleteToolbarItemId(id)], deleteEnabled);
    };

    const scheduleDeleteToolbarItemStateUpdate = () => {
        scheduleToolbarFlushRaf(deleteToolbarFlushRafRef, flushDeleteToolbarItemState);
    };

    const performDeleteSelectedColumn = (field: string) => {
        const grid = innerGridRef.current;
        if (!grid) {
            return;
        }
        grid.editModule?.batchSave();

        const newColumnModels = (grid.columns as ColumnModel[]).filter((col) => col.field !== field);
        const currentData = grid.getDataModule().dataManager.dataSource.json as Record<string, unknown>[];
        currentData.forEach((row) => {
            delete row[field];
        });

        grid.columns = newColumnModels;
        grid.dataSource = [...currentData];
        grid.clearSelection();
        grid.refreshColumns();
        onDataChange?.();
        scheduleDeleteToolbarItemStateUpdate();
    };

    const requestDeleteSelectedColumn = () => {
        const grid = innerGridRef.current;
        if (!grid) {
            return;
        }
        let field = getSelectedDeletableColumnField(grid);
        if (field == null) {
            field = pendingDeleteColumnFieldSnapshotRef.current;
        }
        pendingDeleteColumnFieldSnapshotRef.current = null;
        if (!field) {
            return;
        }
        pendingDeleteColumnFieldRef.current = field;
        setColumnDeleteConfirmOpen(true);
    };

    const onConfirmDeleteColumn = () => {
        const field = pendingDeleteColumnFieldRef.current;
        pendingDeleteColumnFieldRef.current = null;
        setColumnDeleteConfirmOpen(false);
        if (field) {
            performDeleteSelectedColumn(field);
        }
    };

    const onCancelDeleteColumn = () => {
        pendingDeleteColumnFieldRef.current = null;
        setColumnDeleteConfirmOpen(false);
    };

    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        const rowData = args.data as Record<string, unknown> | undefined;

        if (args.row && rowData) {
            const rowNumber = rowData.RowNo as number;
            const rowDefinition = detailedWorkspace.rowSchema?.[rowNumber];
            if (rowDefinition?.disable) {
                args.row.classList.add('disabled-row');
            }
            // When first row is disabled, not editable, has no label, hide it. Same behavior as SMART.
            if (rowNumber === 0 && rowDefinition?.disable && rowDefinition.editable === false && !rowData.Label) {
                args.row.classList.add('hidden-row');
            }
        }
    };

    const rowSelecting = (args: RowSelectingEventArgs) => {
        if (args.target && args.target.classList.contains('e-icons')) {
            innerGridRef.current?.clearSelection();
        }
    };

    const onDataBound = () => {
        const gridComponent = innerGridRef.current;
        if (gridComponent) {
            const gridHeader = gridComponent.element.querySelector<HTMLElement>('.e-gridheader');
            if (gridHeader) {
                gridHeader.tabIndex = 0;
            }

            const fieldToFocus = focusFirstCellFieldAfterColumnAddRef.current;
            if (fieldToFocus) {
                focusFirstCellFieldAfterColumnAddRef.current = null;
                const rowIdx = getFirstEditableDataRowIndex(gridComponent, detailedWorkspace);
                window.requestAnimationFrame(() => {
                    const g = innerGridRef.current;
                    if (!g || g.isEdit || !g.getColumnByField(fieldToFocus)?.field) {
                        return;
                    }
                    g.editModule?.editCell(rowIdx, fieldToFocus);
                });
            }

            scheduleDeleteToolbarItemStateUpdate();
            attachSyncfusionDeleteToolbarFluentTooltip({
                grid: gridComponent,
                attachedMarkerAttr: DELETE_TOOLBAR_FLUENT_TOOLTIP_ATTR,
                getTooltipText: () => deleteToolbarTooltipTextRef.current,
                setTooltipState: setCurrentTooltipState,
                onPointerDownCapture: () => {
                    const g = innerGridRef.current;
                    if (!g) {
                        return;
                    }
                    pendingDeleteColumnFieldSnapshotRef.current = getSelectedDeletableColumnField(g);
                },
            });
        }
    };

    const editCell = (targetCell: HTMLElement) => {
        const grid = innerGridRef.current;
        if (!grid) return;

        const rowCell = targetCell.closest?.('.e-rowcell') as HTMLElement | null;
        if (!rowCell) return;

        const rowIndex = parseInt(rowCell.getAttribute('index') ?? '', 10);
        const columnIndex = parseInt(rowCell.getAttribute('aria-colindex') ?? '', 10) - 1;
        const column = grid.getColumnByIndex(columnIndex);
        const colField = column?.field;
        const firstField = detailedWorkspace?.firstColumn?.field;

        if (colField === ROW_NO_COLUMN_NAME || colField === firstField) {
            return;
        }

        const rowDefinition = detailedWorkspace?.rowSchema?.[rowIndex];

        if (!rowDefinition?.disable && colField) {
            grid.editModule.editCell(rowIndex, colField);

            rowCell.querySelectorAll('input').forEach((input) => {
                input.addEventListener('paste', (event: ClipboardEvent) => customPasteHandler(event, rowDefinition));
            });

            scheduleDeleteToolbarItemStateUpdate();
        }
    };

    const onKeyDown = (e: KeyboardEventArgs) => {
        cancelEditModeOnShiftTab(e, innerGridRef);

        const isDeleteKey = e.key === 'Delete' || e.code === 'Delete';
        if (!isDeleteKey) {
            return;
        }
        if (columnDeleteConfirmOpen || functionalAccess?.amountDataEntryModifyEnabled === false) {
            return;
        }
        const grid = innerGridRef.current;
        if (!grid || getSelectedDeletableColumnField(grid) == null) {
            return;
        }
        requestDeleteSelectedColumn();
        (e as KeyboardEventArgs & { cancel?: boolean }).cancel = true;
        e.preventDefault();
    };

    function created() {
        const grid = innerGridRef.current;
        if (!grid) return;

        enableSingleClickEditOnCells(innerGridRef, editCell);

        queueMicrotask(() => {
            const gridInstance = innerGridRef.current;
            const toolbarModule = getSyncfusionToolbarModule(gridInstance);
            const gridId = gridInstance?.element?.id;
            if (gridInstance && toolbarModule && gridId) {
                toolbarModule.enableItems([syncfusionDeleteToolbarItemId(gridId)], false);
            }
        });

        const notifyIfBatchCellValueChanged = (e: Event) => {
            const target = e.target;
            if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) {
                return;
            }
            if (!target.closest('.e-editedbatchcell')) {
                return;
            }
            if (
                batchEditValueChanged(
                    target.value,
                    activeBatchEditBaselineRef.current,
                    activeBatchEditNumericRef.current,
                    activeBatchEditNumericModelBaselineRef.current
                )
            ) {
                onDataChangeRef.current?.();
            }
        };
        grid.element.addEventListener('input', notifyIfBatchCellValueChanged);
        grid.element.addEventListener('change', notifyIfBatchCellValueChanged);
    }

    const cellEdit = (args: CellEditArgs) => {
        const rowData = args.rowData as any;

        if (rowData?.RowNo) {
            const rowDefinition = detailedWorkspace?.rowSchema?.[rowData?.RowNo as number];
            if (rowDefinition?.disable) {
                args.cancel = true;
                return;
            }
        }
        editValueChangedRef.current = false;

        const column = args.column as ColumnModel | undefined;
        const rowNo = rowData?.RowNo as number | undefined;
        const cellDef = rowNo != null ? detailedWorkspace?.rowSchema?.[rowNo] : undefined;
        if (args.cancel || rowNo == null || !column?.field || column.field === detailedWorkspace?.firstColumn?.field) {
            return;
        }

        scheduleDeleteToolbarItemStateUpdate();

        if (cellDef?.dataType !== ColumnGridDataType.Numeric || cellDef.maxLength == null) {
            return;
        }
        const numericMaxLength = cellDef.maxLength;
        const applyNumericMaxLength = () => {
            const input = innerGridRef.current?.element.querySelector('.e-editedbatchcell input, .e-editedbatchcell textarea') as
                | HTMLInputElement
                | HTMLTextAreaElement
                | null;
            if (input) {
                input.maxLength = numericMaxLength;
            }
        };
        requestAnimationFrame(() => requestAnimationFrame(applyNumericMaxLength));
    };

    const onCellSave = (args: any) => {
        const rowNo = args?.rowData?.RowNo as number | undefined;
        const field = (args?.column as ColumnModel | undefined)?.field ?? (args?.columnName as string | undefined);
        const isNumeric = isNumericDataColumnCell(field, rowNo, detailedWorkspace);
        const changed = isNumeric ? numericValuesDiffer(args?.previousValue, args?.value) : args?.previousValue !== args?.value;

        if (changed) {
            editValueChangedRef.current = true;
            onDataChange?.();
            return;
        }
        editValueChangedRef.current = false;
    };

    const toolbarClick = (args: any) => {
        if (args.item.properties.text?.includes('Add')) {
            args.cancel = true;
            addColumn();
        } else if (args.item.properties.text?.includes('Delete')) {
            args.cancel = true;
            requestDeleteSelectedColumn();
        }
    };

    const addColumns = (count: number, applyDefaultValues: boolean = true) => {
        const grid = innerGridRef.current;
        innerGridRef.current?.editModule.batchSave();

        if (!grid || count <= 0) return;

        const currentColumns = getUpdatedColumns();
        const currentData = grid.getDataModule().dataManager.dataSource.json as Record<string, unknown>[];

        const newColumns: ColumnModel[] = Array.from({ length: count }, (_, index) => {
            const newColumnField = `C${currentColumns.length - 1 + index}`;

            if (applyDefaultValues) {
                currentData.forEach((row) => {
                    const dataType = detailedWorkspace?.rowSchema?.[row.RowNo as number]?.dataType;
                    row[newColumnField] = dataType === ColumnGridDataType.Numeric ? 0 : '';
                });
            }

            return {
                field: newColumnField,
                headerText: '',
                editType: getGridColumnEditType(ColumnType.Text),
                type: ColumnType.Text,
                allowEditing: editOptions.allowEditing,
                width: DATA_COLUMN_MIN_WIDTH,
                minWidth: DATA_COLUMN_MIN_WIDTH,
                isFrozen: false,
                freeze: 'None',
            };
        });

        grid.dataSource = currentData;
        grid.columns = [...(grid.columns as ColumnModel[]), ...newColumns];
        const fieldToFocus = newColumns[newColumns.length - 1]?.field;
        if (fieldToFocus) {
            focusFirstCellFieldAfterColumnAddRef.current = fieldToFocus;
        }
        grid.refreshColumns();
        onDataChange?.();
        scheduleDeleteToolbarItemStateUpdate();
    };

    const addColumn = (applyDefaultValues: boolean = true) => {
        addColumns(1, applyDefaultValues);
    };

    const onCellSaved = (args: CellSaveArgs) => {
        const cell = args.cell as HTMLElement | undefined;
        if (cell) {
            addTooltipEventHandlers(cell);
        }
        const field = args.columnName;
        const rowData = args.rowData as Record<string, unknown> | undefined;
        const rowNo = rowData?.RowNo as number | undefined;
        if (cell && isNumericDataColumnCell(field, rowNo, detailedWorkspace)) {
            cell.textContent = formatNumericCellDisplayForGrid(args.value);
        }
        scheduleDeleteToolbarItemStateUpdate();
    };

    return (
        <>
            <ConfirmationDialogV9
                open={columnDeleteConfirmOpen}
                title={t('deleteColumnConfirmationTitle')}
                content={t('deleteColumnConfirmationMessage')}
                buttons={
                    <>
                        <DialogTrigger>
                            <Button
                                onClick={onCancelDeleteColumn}
                                data-analytics-button-name='confirmation_dialog_cancel'
                                aria-label={t('cancel').toString()}
                            >
                                {t('cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance='primary'
                            onClick={onConfirmDeleteColumn}
                            data-analytics-button-name='confirmation_dialog_save'
                            aria-label={t('delete').toString()}
                        >
                            {t('delete')}
                        </Button>
                    </>
                }
            />
            <div className={styles.tableContentClass}>
                <div className={byColumnStatementClass}>
                    <Tooltip
                        relationship='description'
                        visible={!!currentTooltipState.target}
                        content={currentTooltipState.text ?? ''}
                        appearance='inverted'
                        withArrow
                        positioning={{ target: currentTooltipState.target, position: 'after' }}
                    />
                    <DefaultGrid
                        ref={innerGridRef}
                        gridLines='Vertical'
                        cellEdit={cellEdit}
                        cellSave={onCellSave}
                        dataSource={dataSource}
                        columns={gridColumns}
                        selectionSettings={selectionSettings}
                        rowSelecting={rowSelecting}
                        cellSelected={scheduleDeleteToolbarItemStateUpdate}
                        cellDeselected={scheduleDeleteToolbarItemStateUpdate}
                        rowDataBound={handleRowDataBound}
                        dataBound={onDataBound}
                        cellSaved={onCellSaved}
                        created={created}
                        allowResizing
                        queryCellInfo={onQueryCellInfo}
                        toolbar={toolbarOptions}
                        toolbarClick={toolbarClick}
                        height='100%'
                        width='100%'
                        editSettings={editOptions}
                        keyPressed={onKeyDown}
                    >
                        <Inject services={[Resize, Edit, Toolbar, Freeze]} />
                    </DefaultGrid>
                </div>
            </div>
        </>
    );
});

export default ColumnTableFormViewer;
